import { t } from '@alternativamais/chat-core';
import { useState } from 'react';

import { Avatar } from '../../componentes/Avatar';
import { avisar, avisarErro } from '../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
  SeparadorMenu,
} from '../../componentes/Flutuante';
import { abrirCamada } from '../../chat/camadas';
import { Icone } from '../../componentes/Icone';
import { useCliente, useEstado, useSessao } from '../../chat/contexto';
import { useEu, useModoDesenvolvedor } from '../../chat/ganchos';
import { corDoStatus } from '@alternativamais/chat-core';
import { TextoDoStatus, useStatusPersonalizado } from './perfil/perfil';

type Status = 'online' | 'idle' | 'dnd' | 'invisible';

const OPCOES: Array<{ status: Status; descricao?: string }> = [
  { status: 'online' },
  { status: 'idle' },
  { status: 'dnd', descricao: 'Sem notificações na área de trabalho' },
  {
    status: 'invisible',
    descricao: 'Você aparece offline, mas usa tudo normalmente',
  },
];

/** Rodapé da barra lateral: eu, meu status (C20) e sair. */
export function PainelUsuario() {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const { sair } = useSessao();
  const eu = useEu();
  const status = useEstado((estado) => estado.userSettings?.status ?? 'online');
  const personalizado = useStatusPersonalizado(eu.id);
  const [menu, setMenu] = useState<Posicao | null>(null);
  const visivel = status === 'invisible' ? 'offline' : status;
  const nome = eu.global_name ?? eu.username;
  const fechar = () => setMenu(null);
  return (
    <div className="flex h-14 shrink-0 items-center gap-2 bg-painel px-2">
      <button
        type="button"
        aria-label="Mudar status"
        aria-haspopup="menu"
        aria-expanded={!!menu}
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setMenu(menu ? null : { x: caixa.left, y: caixa.top - 8 });
        }}
        className="flex min-w-0 grow items-center gap-2 rounded-lg p-1 text-left hover:bg-hover"
      >
        <Avatar
          pessoa={eu}
          nome={nome}
          tamanho={32}
          status={visivel}
          fundo="var(--cor-painel)"
        />
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold">{nome}</span>
          {personalizado ? (
            <TextoDoStatus userId={eu.id} className="text-xs text-texto-3" />
          ) : (
            <span className="truncate text-xs text-texto-3">
              {t(`status.${status}`)}
            </span>
          )}
        </span>
      </button>
      <button
        type="button"
        aria-label="Configurações do usuário"
        title="Configurações do usuário"
        onClick={() => abrirCamada({ tipo: 'config-usuario' })}
        className="flex size-8 shrink-0 items-center justify-center rounded-md text-texto-2 hover:bg-hover hover:text-texto"
      >
        <Icone nome="engrenagem" tamanho={20} />
      </button>
      {menu && (
        <Flutuante
          ancora={menu}
          aoFechar={fechar}
          rotulo="Status"
          papel="menu"
          abrir="cima"
        >
          <CaixaMenu largura={280}>
            <div role="group" aria-label="Status" className="flex flex-col">
              {OPCOES.map((opcao) => (
                <ItemMenu
                  key={opcao.status}
                  marcado={status === opcao.status}
                  descricao={opcao.descricao}
                  icone={<BolinhaStatus status={opcao.status} />}
                  aoEscolher={() => {
                    fechar();
                    cliente.setStatus(opcao.status).catch(avisarErro);
                  }}
                >
                  {t(`status.${opcao.status}`)}
                </ItemMenu>
              ))}
            </div>
            <SeparadorMenu />
            <ItemMenu
              icone="sorriso"
              aoEscolher={() => {
                fechar();
                abrirCamada({ tipo: 'status-personalizado' });
              }}
            >
              {personalizado
                ? 'Editar status personalizado'
                : 'Definir status personalizado'}
            </ItemMenu>
            {personalizado && (
              <ItemMenu
                icone="x"
                aoEscolher={() => {
                  fechar();
                  cliente.api
                    .updateUserSettings({ custom_status: null })
                    .then((ajustes) =>
                      cliente.store
                        .getState()
                        .dispatch('USER_SETTINGS_UPDATE', ajustes),
                    )
                    .catch(avisarErro);
                }}
              >
                Limpar status personalizado
              </ItemMenu>
            )}
            <SeparadorMenu />
            {dev && (
              <ItemMenu
                icone="copiar"
                aoEscolher={() => {
                  fechar();
                  void navigator.clipboard
                    ?.writeText(eu.id)
                    .then(() => avisar('ID copiado.', 'info'))
                    .catch(() => avisar('Não deu para copiar.'));
                }}
              >
                Copiar ID do usuário
              </ItemMenu>
            )}
            <ItemMenu
              perigo
              icone="sair"
              aoEscolher={() => {
                fechar();
                void sair();
              }}
            >
              Sair
            </ItemMenu>
          </CaixaMenu>
        </Flutuante>
      )}
    </div>
  );
}

function BolinhaStatus({ status }: { status: Status }) {
  return (
    <span aria-hidden="true" className="flex w-4 shrink-0 justify-center">
      {status === 'invisible' ? (
        <span className="size-2.5 rounded-full border-[3px] border-texto-3" />
      ) : (
        <span
          className="size-2.5 rounded-full"
          style={{ background: corDoStatus(status) }}
        />
      )}
    </span>
  );
}
